import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { LoggerService } from '../core/services/logger.service';
import { environment } from '../../environments/environment';
import {
  OrdenesTransporteFilterDto,
  OrdenesTransporteResponseDto,
  OrdenesTransporteItemDto,
  OrdenesTransporteConsolidadoItemDto,
  ClienteTransporteComboDto,
  ExportadorTransporteComboDto,
  NaveTransporteComboDto,
  TemporadaTransporteComboDto,
  TransportOrderDetailResponseDto
} from '@opb-workspace/shared';

/**
 * Servicio para gestionar Órdenes de Transporte
 * Maneja la comunicación con el backend y proporciona métodos para:
 * - Listado con filtros y paginación
 * - Exportación a Excel (futuro)
 * - Descarga de documentos (futuro)
 */
@Injectable({
  providedIn: 'root'
})
export class OrdenesTransporteService {
  private readonly baseUrl = `${environment.apiUrl}/v1/ordenes-transporte`;

  constructor(
    private http: HttpClient,
    private logger: LoggerService
  ) { }

  private getAuthToken(): string | null {
    return localStorage.getItem('access_token') || localStorage.getItem('token');
  }

  private getAuthHeaders(): HttpHeaders {
    const token = this.getAuthToken();
    return new HttpHeaders({
      Authorization: token ? `Bearer ${token}` : ''
    });
  }

  /**
   * Obtiene lista paginada de órdenes de transporte
   * Soporta filtros múltiples y paginación server-side
   */
  getOrdenes(filters: OrdenesTransporteFilterDto): Observable<OrdenesTransporteResponseDto> {
    this.logger.info('🔄 OrdenesTransporteService: Obteniendo órdenes con filtros:', JSON.stringify(filters));

    // Construir parámetros HTTP
    let params = new HttpParams();

    if (filters.clienteId) {
      params = params.set('clienteId', filters.clienteId.toString());
    }
    if (filters.exportadorId) {
      params = params.set('exportadorId', filters.exportadorId.toString());
    }
    if (filters.nave?.trim()) {
      params = params.set('nave', filters.nave.trim());
    }
    if (filters.temporadaId) {
      params = params.set('temporadaId', filters.temporadaId.toString());
    }
    if (filters.dateFrom) {
      params = params.set('dateFrom', filters.dateFrom);
    }
    if (filters.dateTo) {
      params = params.set('dateTo', filters.dateTo);
    }
    if (filters.referenciaCliente?.trim()) {
      params = params.set('referenciaCliente', filters.referenciaCliente.trim());
    }
    if (filters.contenedor?.trim()) {
      params = params.set('contenedor', filters.contenedor.trim());
    }
    if (filters.dus?.trim()) {
      params = params.set('dus', filters.dus.trim());
    }
    if (filters.licensePlate?.trim()) {
      params = params.set('licensePlate', filters.licensePlate.trim());
    }
    if (filters.nroLiquidacion) {
      params = params.set('nroLiquidacion', filters.nroLiquidacion.toString());
    }
    if (filters.booking?.trim()) {
      params = params.set('booking', filters.booking.trim());
    }
    if (filters.page) {
      params = params.set('page', filters.page.toString());
    }
    if (filters.limit) {
      params = params.set('limit', filters.limit.toString());
    }
    if (filters.orderBy) {
      params = params.set('orderBy', filters.orderBy);
    }
    if (filters.orderDirection) {
      params = params.set('orderDirection', filters.orderDirection);
    }

    return this.http.get<OrdenesTransporteResponseDto>(this.baseUrl, { params, headers: this.getAuthHeaders() })
      .pipe(
        retry(2), // Reintentar hasta 2 veces en caso de error
        catchError((error: HttpErrorResponse) => {
          this.logger.error('❌ Error obteniendo órdenes de transporte:', new Error(error.message || 'Error desconocido'));
          return this.handleError(error);
        })
      );
  }

  /**
   * Obtiene el Excel Consolidado: una fila por contenedor, sin paginación.
   * Reutiliza los mismos filtros del listado.
   */
  getOrdenesConsolidadoExcel(filters: OrdenesTransporteFilterDto): Observable<{
    data: OrdenesTransporteConsolidadoItemDto[];
    total: number;
    timestamp: string;
  }> {
    this.logger.info('🔄 OrdenesTransporteService: Obteniendo Excel Consolidado con filtros:', JSON.stringify(filters));

    let params = new HttpParams();

    if (filters.clienteId) {
      params = params.set('clienteId', filters.clienteId.toString());
    }
    if (filters.exportadorId) {
      params = params.set('exportadorId', filters.exportadorId.toString());
    }
    if (filters.nave?.trim()) {
      params = params.set('nave', filters.nave.trim());
    }
    if (filters.temporadaId) {
      params = params.set('temporadaId', filters.temporadaId.toString());
    }
    if (filters.dateFrom) {
      params = params.set('dateFrom', filters.dateFrom);
    }
    if (filters.dateTo) {
      params = params.set('dateTo', filters.dateTo);
    }
    if (filters.referenciaCliente?.trim()) {
      params = params.set('referenciaCliente', filters.referenciaCliente.trim());
    }
    if (filters.contenedor?.trim()) {
      params = params.set('contenedor', filters.contenedor.trim());
    }
    if (filters.dus?.trim()) {
      params = params.set('dus', filters.dus.trim());
    }
    if (filters.licensePlate?.trim()) {
      params = params.set('licensePlate', filters.licensePlate.trim());
    }
    if (filters.nroLiquidacion) {
      params = params.set('nroLiquidacion', filters.nroLiquidacion.toString());
    }
    if (filters.booking?.trim()) {
      params = params.set('booking', filters.booking.trim());
    }

    return this.http.get<{
      data: OrdenesTransporteConsolidadoItemDto[];
      total: number;
      timestamp: string;
    }>(`${this.baseUrl}/consolidado-excel`, { params, headers: this.getAuthHeaders() })
      .pipe(
        retry(2),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('❌ Error obteniendo Excel Consolidado:', new Error(error.message || 'Error desconocido'));
          return this.handleError(error);
        })
      );
  }

  /**
   * Obtiene clientes que tienen órdenes de transporte
   */
  getClientsCombo(): Observable<{
    data: ClienteTransporteComboDto[];
    total: number;
    timestamp: string;
  }> {
    const url = `${this.baseUrl}/filters/clients`;
    return this.http.get<{
      data: ClienteTransporteComboDto[];
      total: number;
      timestamp: string;
    }>(
      url,
      { headers: this.getAuthHeaders() }
    ).pipe(
      retry(2),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('❌ Error obteniendo clientes con órdenes de transporte:', new Error(error.message || 'Error desconocido'));
        return this.handleError(error);
      })
    );
  }

  /**
   * Obtiene naves que tienen órdenes de transporte
   */
  getVesselsCombo(): Observable<{
    data: NaveTransporteComboDto[];
    total: number;
    timestamp: string;
  }> {
    const url = `${this.baseUrl}/filters/vessels`;
    return this.http.get<{
      data: NaveTransporteComboDto[];
      total: number;
      timestamp: string;
    }>(
      url,
      { headers: this.getAuthHeaders() }
    ).pipe(
      retry(2),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('❌ Error obteniendo naves con órdenes de transporte:', new Error(error.message || 'Error desconocido'));
        return this.handleError(error);
      })
    );
  }

  /**
   * Obtiene exportadores que tienen órdenes de transporte
   */
  getExportersCombo(): Observable<{
    data: ExportadorTransporteComboDto[];
    total: number;
    timestamp: string;
  }> {
    const url = `${this.baseUrl}/filters/exporters`;
    return this.http.get<{
      data: ExportadorTransporteComboDto[];
      total: number;
      timestamp: string;
    }>(
      url,
      { headers: this.getAuthHeaders() }
    ).pipe(
      retry(2),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('❌ Error obteniendo exportadores con órdenes de transporte:', new Error(error.message || 'Error desconocido'));
        return this.handleError(error);
      })
    );
  }

  /**
   * Obtiene temporadas que tienen órdenes de transporte
   */
  getSeasonsCombo(): Observable<{
    data: TemporadaTransporteComboDto[];
    total: number;
    timestamp: string;
  }> {
    const url = `${this.baseUrl}/filters/seasons`;
    return this.http.get<{
      data: TemporadaTransporteComboDto[];
      total: number;
      timestamp: string;
    }>(
      url,
      { headers: this.getAuthHeaders() }
    ).pipe(
      retry(2),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('❌ Error obteniendo temporadas con órdenes de transporte:', new Error(error.message || 'Error desconocido'));
        return this.handleError(error);
      })
    );
  }

  /**
   * Obtiene el detalle de una orden de transporte.
   */
  getTransportOrderDetail(id: number): Observable<TransportOrderDetailResponseDto> {
    this.logger.info(`🔍 OrdenesTransporteService: Obteniendo detalle para la orden ${id}`);
    const url = `${this.baseUrl}/${id}`;
    return this.http.get<TransportOrderDetailResponseDto>(url, { headers: this.getAuthHeaders() })
      .pipe(
        retry(2),
        catchError((error: HttpErrorResponse) => {
          this.logger.error(`❌ Error obteniendo detalle de la orden ${id}:`, new Error(error.message || 'Error desconocido'));
          return this.handleError(error);
        })
      );
  }

  /**
   * Maneja errores HTTP de manera consistente
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Error desconocido';

    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Error del lado del servidor
      errorMessage = `Error ${error.status}: ${error.message}`;
      if (error.error?.message) {
        errorMessage = error.error.message;
      }
    }

    this.logger.error('❌ Error en OrdenesTransporteService:', new Error(errorMessage));
    return throwError(() => new Error(errorMessage));
  }
}

